import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  Download,
  User,
  Mail,
  Phone,
  Calendar,
  MapPin,
  Euro,
  Shirt,
  Heart,
  FileText,
  X,
  Check,
  Users,
  AlertCircle,
} from "lucide-react";
import axios from "axios";
import * as XLSX from "xlsx";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status Labels & Colors
const campStatusLabels = {
  confirmed: "Bestätigt",
  pending: "Ausstehend",
  cancelled: "Storniert",
};

const campStatusColors = {
  confirmed: "bg-green-500/20 text-green-400 border-green-500/30",
  pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  cancelled: "bg-red-500/20 text-red-400 border-red-500/30",
};

const paymentStatusLabels = {
  pending: "Ausstehend",
  paid: "Bezahlt",
  refunded: "Erstattet",
};

const paymentStatusColors = {
  pending: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  paid: "bg-green-500/20 text-green-400 border-green-500/30",
  refunded: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

export default function CampRegistrationsTab({ token }) {
  const [registrations, setRegistrations] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [campFilter, setCampFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  
  // Dialog states
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  
  // Diagnose state
  const [diagnoseCamp, setDiagnoseCamp] = useState(null);
  const [diagnoseData, setDiagnoseData] = useState(null);
  const [isFixing, setIsFixing] = useState(false);

  // Load data on mount and when token changes
  useEffect(() => {
    if (token) {
      loadData();
    }
  }, [token]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const headers = { "X-Admin-Token": token };
      const [registrationsRes, statsRes] = await Promise.all([
        axios.get(`${API}/camps/registrations`, { headers }),
        axios.get(`${API}/camps/stats`, { headers }),
      ]);
      setRegistrations(registrationsRes.data.registrations || []);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Sitzung abgelaufen");
      } else {
        toast.error("Fehler beim Laden der Camp-Daten");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Filter registrations
  const filteredRegistrations = registrations.filter((reg) => {
    // Search filter
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm || 
      reg.participant?.first_name?.toLowerCase().includes(searchLower) ||
      reg.participant?.last_name?.toLowerCase().includes(searchLower) ||
      reg.parent?.first_name?.toLowerCase().includes(searchLower) ||
      reg.parent?.last_name?.toLowerCase().includes(searchLower) ||
      reg.parent?.email?.toLowerCase().includes(searchLower) ||
      reg.parent?.phone?.includes(searchTerm) ||
      reg.registration_id?.toLowerCase().includes(searchLower);
    
    // Camp filter
    const matchesCamp = campFilter === "all" || reg.camp_id === campFilter;
    
    // Payment status filter
    const matchesPayment = paymentStatusFilter === "all" || reg.payment_status === paymentStatusFilter;
    
    // Status filter
    const matchesStatus = statusFilter === "all" || reg.status === statusFilter;
    
    return matchesSearch && matchesCamp && matchesPayment && matchesStatus;
  });

  // Update registration status
  const updateStatus = async (registrationId, newStatus) => {
    try {
      await axios.patch(
        `${API}/camps/registrations/${registrationId}/status`,
        { status: newStatus },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Status aktualisiert");
      loadData();
      // Update selected registration if dialog is open
      if (selectedRegistration?.registration_id === registrationId) {
        setSelectedRegistration(prev => ({ ...prev, status: newStatus }));
      }
    } catch (error) {
      toast.error("Fehler beim Aktualisieren des Status");
    }
  };

  // Diagnose camp
  const runDiagnose = async (campId) => {
    setDiagnoseCamp(campId);
    setDiagnoseData(null);
    try {
      const res = await axios.get(`${API}/camps/diagnose/${campId}`, {
        headers: { "X-Admin-Token": token }
      });
      setDiagnoseData(res.data);
    } catch (error) {
      toast.error("Fehler bei der Diagnose");
      setDiagnoseCamp(null);
    }
  };

  // Fix camp status
  const fixCampStatus = async (campId) => {
    setIsFixing(true);
    try {
      const res = await axios.post(`${API}/camps/fix/${campId}`, {}, {
        headers: { "X-Admin-Token": token }
      });
      toast.success(res.data.message);
      setDiagnoseCamp(null);
      setDiagnoseData(null);
      loadData();
    } catch (error) {
      toast.error("Fehler beim Beheben");
    } finally {
      setIsFixing(false);
    }
  };

  // Update payment status
  const updatePaymentStatus = async (registrationId, newStatus) => {
    try {
      await axios.patch(
        `${API}/camps/registrations/${registrationId}/payment`,
        { payment_status: newStatus },
        { headers: { "X-Admin-Token": token } }
      );
      toast.success("Zahlungsstatus aktualisiert");
      loadData();
      // Update selected registration if dialog is open
      if (selectedRegistration?.registration_id === registrationId) {
        setSelectedRegistration(prev => ({ ...prev, payment_status: newStatus }));
      }
    } catch (error) {
      toast.error("Fehler beim Aktualisieren des Zahlungsstatus");
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredRegistrations.length === 0) {
      toast.error("Keine Daten zum Exportieren");
      return;
    }

    const data = filteredRegistrations.map((reg) => ({
      "Anmelde-Nr.": reg.registration_id,
      "Camp": reg.camp_name,
      "Camp-Datum": reg.camp_date,
      "Teilnehmer Vorname": reg.participant?.first_name,
      "Teilnehmer Nachname": reg.participant?.last_name,
      "Geburtsdatum": reg.participant?.birth_date,
      "Geschlecht": reg.participant?.gender === "male" ? "Männlich" : reg.participant?.gender === "female" ? "Weiblich" : "Divers",
      "Trikotgröße": reg.participant?.jersey_size,
      "Hosengröße": reg.participant?.shorts_size,
      "Verein": reg.participant?.current_club || "-",
      "Position": reg.participant?.position || "-",
      "Torwart": reg.participant?.is_goalkeeper ? "Ja" : "Nein",
      "Eltern Vorname": reg.parent?.first_name,
      "Eltern Nachname": reg.parent?.last_name,
      "E-Mail": reg.parent?.email,
      "Telefon": reg.parent?.phone,
      "Straße": reg.parent?.street || "-",
      "Hausnr.": reg.parent?.house_number || "-",
      "PLZ": reg.parent?.postal_code || "-",
      "Ort": reg.parent?.city || "-",
      "Notfallkontakt Name": reg.health?.emergency_contact_name,
      "Notfallkontakt Tel.": reg.health?.emergency_contact_phone,
      "Hinweise": reg.health?.notes || "-",
      "Alleine nach Hause": reg.health?.can_go_home_alone ? "Ja" : "Nein",
      "Abholer": reg.health?.pickup_person || "-",
      "AGB akzeptiert": reg.consent?.agb_accepted ? "Ja" : "Nein",
      "Datenschutz akzeptiert": reg.consent?.privacy_accepted ? "Ja" : "Nein",
      "Newsletter": reg.consent?.marketing_consent ? "Ja" : "Nein",
      "Preis": `${reg.camp_price}€`,
      "Zahlungsstatus": paymentStatusLabels[reg.payment_status] || "Ausstehend",
      "Status": campStatusLabels[reg.status] || "Ausstehend",
      "Anmeldedatum": reg.created_at ? new Date(reg.created_at).toLocaleDateString("de-DE") : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Camp-Anmeldungen");
    
    // Auto-width columns
    const colWidths = Object.keys(data[0] || {}).map(key => ({
      wch: Math.max(key.length, ...data.map(row => String(row[key] || "").length)) + 2
    }));
    ws["!cols"] = colWidths;

    const fileName = `Camp-Anmeldungen_${new Date().toISOString().split("T")[0]}.xlsx`;
    XLSX.writeFile(wb, fileName);
    toast.success(`Export erfolgreich: ${fileName}`);
  };

  // Get unique camps for filter
  const uniqueCamps = [...new Set(registrations.map(r => r.camp_id))].map(campId => {
    const reg = registrations.find(r => r.camp_id === campId);
    return { id: campId, name: reg?.camp_name || campId };
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-4">
        <h2 className="font-['Bebas_Neue'] text-xl text-white">CAMP-ANMELDUNGEN</h2>
        <div className="flex items-center gap-2">
          <Button 
            onClick={exportToExcel} 
            variant="outline" 
            size="sm" 
            className="border-[#c1ff72]/30 text-[#c1ff72] hover:bg-[#c1ff72]/10"
            data-testid="export-excel-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Excel Export
          </Button>
          <Button 
            onClick={loadData} 
            variant="outline" 
            size="sm" 
            className="border-white/20 text-white"
            data-testid="refresh-camps-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Aktualisieren
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#1A1A1A] border border-white/10 p-4">
            <p className="text-[#71717A] text-sm">Gesamt</p>
            <p className="font-['Bebas_Neue'] text-2xl text-white">{stats.total_registrations}</p>
          </div>
          {stats.camps?.map((camp) => (
            <div key={camp.camp_id} className="bg-[#1A1A1A] border border-white/10 p-4 relative group">
              <button
                onClick={() => runDiagnose(camp.camp_id)}
                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 text-xs text-[#bea777] hover:text-[#c1ff72] transition-opacity"
                title="Diagnose"
              >
                <AlertCircle className="w-4 h-4" />
              </button>
              <p className="text-[#71717A] text-sm truncate" title={camp.name}>{camp.name}</p>
              <p className="font-['Bebas_Neue'] text-2xl text-white">
                {camp.registered}/{camp.max_participants}
              </p>
              <p className="text-xs text-[#c1ff72]">{camp.remaining_spots} Plätze frei</p>
              {camp.early_bird_slots > 0 && (
                <p className="text-xs text-[#22c55e] mt-1">
                  Frühbucher: {Math.min(camp.registered, camp.early_bird_slots)}/{camp.early_bird_slots}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#1A1A1A] border border-white/10 p-4 rounded-lg mb-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          {/* Search */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#71717A]" />
              <Input
                placeholder="Suche nach Name, E-Mail, Telefon..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-[#0A0A0A] border-white/10 text-white"
                data-testid="camp-search-input"
              />
            </div>
          </div>

          {/* Camp Filter */}
          <Select value={campFilter} onValueChange={setCampFilter}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="camp-filter-select">
              <SelectValue placeholder="Alle Camps" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all" className="text-white">Alle Camps</SelectItem>
              {uniqueCamps.map((camp) => (
                <SelectItem key={camp.id} value={camp.id} className="text-white">
                  {camp.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Payment Status Filter */}
          <Select value={paymentStatusFilter} onValueChange={setPaymentStatusFilter}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="payment-filter-select">
              <SelectValue placeholder="Zahlungsstatus" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all" className="text-white">Alle Zahlungen</SelectItem>
              <SelectItem value="pending" className="text-white">Ausstehend</SelectItem>
              <SelectItem value="paid" className="text-white">Bezahlt</SelectItem>
              <SelectItem value="refunded" className="text-white">Erstattet</SelectItem>
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="status-filter-select">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="bg-[#1A1A1A] border-white/10">
              <SelectItem value="all" className="text-white">Alle Status</SelectItem>
              <SelectItem value="confirmed" className="text-white">Bestätigt</SelectItem>
              <SelectItem value="pending" className="text-white">Ausstehend</SelectItem>
              <SelectItem value="cancelled" className="text-white">Storniert</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        {/* Filter Summary */}
        <div className="mt-3 flex items-center gap-2 text-sm text-[#71717A]">
          <span>{filteredRegistrations.length} von {registrations.length} Anmeldungen</span>
          {(searchTerm || campFilter !== "all" || paymentStatusFilter !== "all" || statusFilter !== "all") && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => {
                setSearchTerm("");
                setCampFilter("all");
                setPaymentStatusFilter("all");
                setStatusFilter("all");
              }}
              className="text-[#c1ff72] hover:text-[#d4ff9a] h-auto py-1"
            >
              Filter zurücksetzen
            </Button>
          )}
        </div>
      </div>

      {/* Registrations Table */}
      {isLoading ? (
        <div className="text-center py-12 text-[#A1A1AA]">Laden...</div>
      ) : filteredRegistrations.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-12 h-12 text-[#71717A] mx-auto mb-4" />
          <p className="text-[#A1A1AA]">
            {registrations.length === 0 ? "Noch keine Anmeldungen" : "Keine Treffer für diese Filter"}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Teilnehmer</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Camp</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Eltern</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Kontakt</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Größen</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Preis</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Zahlung</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Status</th>
                <th className="text-left py-3 px-2 text-[#71717A] font-medium">Datum</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistrations.map((reg) => (
                <tr 
                  key={reg.registration_id} 
                  className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                  onClick={() => {
                    setSelectedRegistration(reg);
                    setShowDetailDialog(true);
                  }}
                  data-testid={`camp-reg-row-${reg.registration_id}`}
                >
                  <td className="py-3 px-2">
                    <p className="text-white font-medium">
                      {reg.participant?.first_name} {reg.participant?.last_name}
                    </p>
                    <p className="text-[#71717A] text-xs">{reg.registration_id}</p>
                  </td>
                  <td className="py-3 px-2">
                    <p className="text-white text-xs">{reg.camp_name?.replace("Sommer Fußball Camp - ", "")}</p>
                    <p className="text-[#71717A] text-xs">{reg.camp_date}</p>
                  </td>
                  <td className="py-3 px-2">
                    <p className="text-white">{reg.parent?.first_name} {reg.parent?.last_name}</p>
                  </td>
                  <td className="py-3 px-2">
                    <p className="text-white text-xs">{reg.parent?.email}</p>
                    <p className="text-[#71717A] text-xs">{reg.parent?.phone}</p>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-1">
                      <Shirt className="w-3 h-3 text-[#c1ff72]" />
                      <span className="text-white text-xs">{reg.participant?.jersey_size}</span>
                      <span className="text-[#71717A] text-xs">/</span>
                      <span className="text-white text-xs">{reg.participant?.shorts_size}</span>
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-1">
                      <span className="text-[#c1ff72] font-medium">{reg.camp_price}€</span>
                      {reg.is_early_bird && (
                        <span className="text-[#22c55e] text-xs px-1 bg-[#22c55e]/20 rounded">-10%</span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2">
                    <Badge className={`${paymentStatusColors[reg.payment_status] || paymentStatusColors.pending} border text-xs`}>
                      {paymentStatusLabels[reg.payment_status] || "Ausstehend"}
                    </Badge>
                  </td>
                  <td className="py-3 px-2">
                    <Badge className={`${campStatusColors[reg.status] || campStatusColors.pending} border text-xs`}>
                      {campStatusLabels[reg.status] || "Ausstehend"}
                    </Badge>
                  </td>
                  <td className="py-3 px-2 text-[#71717A] text-xs">
                    {reg.created_at ? new Date(reg.created_at).toLocaleDateString("de-DE") : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="bg-[#121212] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-2xl flex items-center gap-2">
              CAMP-ANMELDUNG
              {selectedRegistration && (
                <span className="text-[#c1ff72] text-base font-normal ml-2">
                  {selectedRegistration.registration_id}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {selectedRegistration && (
            <div className="space-y-6">
              {/* Camp Info */}
              <div className="bg-[#c1ff72]/10 border border-[#c1ff72]/30 p-4 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-[#c1ff72]" />
                  <h3 className="text-[#c1ff72] font-medium">{selectedRegistration.camp_name}</h3>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <p className="text-white"><MapPin className="w-3 h-3 inline mr-1" />{selectedRegistration.camp_location || "Meinerzhagen"}</p>
                  <p className="text-white"><Calendar className="w-3 h-3 inline mr-1" />{selectedRegistration.camp_date}</p>
                </div>
                <div className="mt-3 pt-3 border-t border-[#c1ff72]/20">
                  <div className="flex items-center justify-between">
                    <span className="text-white/70 text-sm">Preis:</span>
                    {selectedRegistration.is_early_bird ? (
                      <div className="flex items-center gap-2">
                        <span className="text-white/50 line-through text-sm">{selectedRegistration.original_price || 180}€</span>
                        <span className="text-[#22c55e] font-bold">{selectedRegistration.camp_price}€</span>
                        <span className="bg-[#22c55e]/20 text-[#22c55e] text-xs px-2 py-0.5 rounded">10% Frühbucher</span>
                      </div>
                    ) : (
                      <span className="text-[#c1ff72] font-bold">{selectedRegistration.camp_price}€</span>
                    )}
                  </div>
                  {selectedRegistration.discount_amount > 0 && (
                    <p className="text-[#22c55e] text-xs mt-1">Rabatt: -{selectedRegistration.discount_amount}€</p>
                  )}
                </div>
              </div>

              {/* Participant */}
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4 text-[#c1ff72]" />
                  Teilnehmer
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-[#71717A]">Name</p>
                    <p className="text-white">{selectedRegistration.participant?.first_name} {selectedRegistration.participant?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">Geburtsdatum</p>
                    <p className="text-white">{selectedRegistration.participant?.birth_date}</p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">Geschlecht</p>
                    <p className="text-white">
                      {selectedRegistration.participant?.gender === "male" ? "Männlich" : 
                       selectedRegistration.participant?.gender === "female" ? "Weiblich" : "Divers"}
                    </p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">Trikotgröße</p>
                    <p className="text-white font-medium">{selectedRegistration.participant?.jersey_size}</p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">Hosengröße</p>
                    <p className="text-white font-medium">{selectedRegistration.participant?.shorts_size}</p>
                  </div>
                  {selectedRegistration.participant?.current_club && (
                    <div>
                      <p className="text-[#71717A]">Verein</p>
                      <p className="text-white">{selectedRegistration.participant.current_club}</p>
                    </div>
                  )}
                  {selectedRegistration.participant?.position && (
                    <div>
                      <p className="text-[#71717A]">Position</p>
                      <p className="text-white capitalize">{selectedRegistration.participant.position}</p>
                    </div>
                  )}
                  {selectedRegistration.participant?.is_goalkeeper && (
                    <div>
                      <p className="text-[#71717A]">Torwart</p>
                      <p className="text-[#c1ff72]">Ja</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Parent */}
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4 text-[#c1ff72]" />
                  Eltern/Erziehungsberechtigte
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#71717A]">Name</p>
                    <p className="text-white">{selectedRegistration.parent?.first_name} {selectedRegistration.parent?.last_name}</p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">E-Mail</p>
                    <p className="text-white">{selectedRegistration.parent?.email}</p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">Telefon</p>
                    <p className="text-white">{selectedRegistration.parent?.phone}</p>
                  </div>
                  {(selectedRegistration.parent?.street || selectedRegistration.parent?.city) && (
                    <div>
                      <p className="text-[#71717A]">Adresse</p>
                      <p className="text-white">
                        {selectedRegistration.parent?.street} {selectedRegistration.parent?.house_number}<br />
                        {selectedRegistration.parent?.postal_code} {selectedRegistration.parent?.city}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Health */}
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Heart className="w-4 h-4 text-[#c1ff72]" />
                  Gesundheit & Notfall
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-[#71717A]">Notfallkontakt</p>
                    <p className="text-white">{selectedRegistration.health?.emergency_contact_name}</p>
                    <p className="text-[#A1A1AA]">{selectedRegistration.health?.emergency_contact_phone}</p>
                  </div>
                  <div>
                    <p className="text-[#71717A]">Alleine nach Hause</p>
                    <p className="text-white">{selectedRegistration.health?.can_go_home_alone ? "Ja" : "Nein"}</p>
                    {!selectedRegistration.health?.can_go_home_alone && selectedRegistration.health?.pickup_person && (
                      <p className="text-[#A1A1AA]">Abholer: {selectedRegistration.health.pickup_person}</p>
                    )}
                  </div>
                  {selectedRegistration.health?.notes && (
                    <div className="col-span-2">
                      <p className="text-[#71717A]">Hinweise (Allergien, etc.)</p>
                      <p className="text-white bg-[#0A0A0A] p-2 mt-1 rounded">{selectedRegistration.health.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Payment & Status */}
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <Euro className="w-4 h-4 text-[#c1ff72]" />
                  Zahlung & Status
                </h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Preis</p>
                    <p className="text-[#c1ff72] font-['Bebas_Neue'] text-3xl">{selectedRegistration.camp_price}€</p>
                  </div>
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Anmeldedatum</p>
                    <p className="text-white">
                      {selectedRegistration.created_at 
                        ? new Date(selectedRegistration.created_at).toLocaleString("de-DE") 
                        : "-"}
                    </p>
                  </div>
                </div>

                {/* Status Management */}
                <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/10">
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Zahlungsstatus</p>
                    <Select 
                      value={selectedRegistration.payment_status || "pending"} 
                      onValueChange={(v) => updatePaymentStatus(selectedRegistration.registration_id, v)}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="detail-payment-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="pending" className="text-white">Ausstehend</SelectItem>
                        <SelectItem value="paid" className="text-white">Bezahlt</SelectItem>
                        <SelectItem value="refunded" className="text-white">Erstattet</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <p className="text-[#71717A] text-sm mb-2">Anmeldestatus</p>
                    <Select 
                      value={selectedRegistration.status || "confirmed"} 
                      onValueChange={(v) => updateStatus(selectedRegistration.registration_id, v)}
                    >
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="detail-registration-status">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="pending" className="text-white">Ausstehend</SelectItem>
                        <SelectItem value="confirmed" className="text-white">Bestätigt</SelectItem>
                        <SelectItem value="cancelled" className="text-white">Storniert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Quick Actions */}
                {selectedRegistration.payment_status === "pending" && (
                  <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded">
                    <div className="flex items-center gap-2 text-orange-400 text-sm mb-2">
                      <AlertCircle className="w-4 h-4" />
                      <span>Zahlung ausstehend</span>
                    </div>
                    <Button
                      onClick={() => updatePaymentStatus(selectedRegistration.registration_id, "paid")}
                      className="bg-green-600 hover:bg-green-700 text-white rounded-none text-sm"
                      size="sm"
                      data-testid="mark-camp-paid-btn"
                    >
                      <Check className="w-4 h-4 mr-1" />
                      Als bezahlt markieren
                    </Button>
                  </div>
                )}
              </div>

              {/* Consent */}
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-[#c1ff72]" />
                  Zustimmungen
                </h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    {selectedRegistration.consent?.agb_accepted 
                      ? <Check className="w-4 h-4 text-green-400" /> 
                      : <X className="w-4 h-4 text-red-400" />}
                    <span className="text-white">AGB</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedRegistration.consent?.privacy_accepted 
                      ? <Check className="w-4 h-4 text-green-400" /> 
                      : <X className="w-4 h-4 text-red-400" />}
                    <span className="text-white">Datenschutz</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {selectedRegistration.consent?.marketing_consent 
                      ? <Check className="w-4 h-4 text-green-400" /> 
                      : <X className="w-4 h-4 text-[#71717A]" />}
                    <span className="text-white">Newsletter</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDetailDialog(false)}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diagnose Modal */}
      <Dialog open={!!diagnoseCamp} onOpenChange={() => { setDiagnoseCamp(null); setDiagnoseData(null); }}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Bebas_Neue'] text-xl text-white">
              Camp-Diagnose
            </DialogTitle>
          </DialogHeader>

          {!diagnoseData ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-[#c1ff72]" />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-[#1A1A1A] p-4 rounded-lg">
                <h4 className="text-white font-medium mb-2">{diagnoseData.camp_name}</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-[#71717A]">Max. Teilnehmer:</div>
                  <div className="text-white">{diagnoseData.diagnose.effective_max_participants}</div>
                  
                  <div className="text-[#71717A]">Aktive Anmeldungen:</div>
                  <div className="text-white">{diagnoseData.diagnose.active_registrations}</div>
                  
                  <div className="text-[#71717A]">Freie Plätze:</div>
                  <div className="text-[#c1ff72]">{diagnoseData.diagnose.remaining_spots}</div>
                  
                  <div className="text-[#71717A]">Status Override:</div>
                  <div className={diagnoseData.diagnose.status_override === "sold_out" ? "text-red-400" : "text-white"}>
                    {diagnoseData.diagnose.status_override || "(automatisch)"}
                  </div>
                  
                  <div className="text-[#71717A]">Berechneter Status:</div>
                  <div className="text-white">{diagnoseData.diagnose.calculated_status.status}</div>
                  
                  <div className="text-[#71717A]">Genutzter Status:</div>
                  <div className={diagnoseData.diagnose.used_status.can_register ? "text-green-400" : "text-red-400"}>
                    {diagnoseData.diagnose.used_status.status}
                    {diagnoseData.diagnose.used_status.can_register ? " (buchbar)" : " (nicht buchbar)"}
                  </div>
                </div>
              </div>

              {diagnoseData.problem_detected && (
                <div className="bg-red-500/20 border border-red-500/30 p-4 rounded-lg">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-400 mt-0.5" />
                    <div>
                      <h4 className="text-red-400 font-medium">Problem erkannt</h4>
                      <p className="text-red-400/80 text-sm mt-1">{diagnoseData.fix_suggestion}</p>
                    </div>
                  </div>
                </div>
              )}

              {!diagnoseData.problem_detected && (
                <div className="bg-green-500/20 border border-green-500/30 p-4 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-400" />
                    <span className="text-green-400">Keine Probleme erkannt</span>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            {diagnoseData?.problem_detected && (
              <Button
                onClick={() => fixCampStatus(diagnoseCamp)}
                disabled={isFixing}
                className="bg-[#c1ff72] hover:bg-[#d4ff8f] text-black"
              >
                {isFixing ? "Behebe..." : "Problem beheben"}
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => { setDiagnoseCamp(null); setDiagnoseData(null); }}
              className="border-white/20 text-white hover:bg-white/10"
            >
              Schließen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
